"use client";

import { cn } from "@ui/lib/utils";
import { useEffect, useRef } from "react";

interface WordRendererProps {
  compact?: boolean;
  fadeUnspoken?: boolean;
  hasBeenSpoken?: boolean;
  highlighted: boolean;
  onHighlight?: (element: HTMLElement) => void;
  showMetadata?: boolean;
  word: string;
  wordIndex: number;
}

export const isBracketedPhrase = (text: string) => text.match(/^\[([^\]]*)\]$/);

export const WordRenderer = ({
  word,
  highlighted,
  wordIndex,
  onHighlight,
  compact,
  fadeUnspoken,
  hasBeenSpoken = true,
  showMetadata,
}: WordRendererProps) => {
  const ref = useRef<HTMLSpanElement>(null);

  // Handle highlighting side effects
  useEffect(() => {
    if (highlighted && ref.current) {
      onHighlight?.(ref.current);
    }
  }, [highlighted, onHighlight]);

  const bracketMatch = isBracketedPhrase(word);
  const shouldFade = fadeUnspoken && !hasBeenSpoken;

  if (bracketMatch) {
    // Convert underscores back to spaces for display
    const cleanedWord = bracketMatch[1].replace(/_/g, " ");
    return (
      <span
        className={cn(
          "inline-flex h-24 items-center rounded-md border border-[rgba(255,255,255,0.4)] px-5",
          showMetadata ? "f-paragraph-01" : compact ? "f-description-02" : "f-description-03",
          shouldFade && "opacity-40",
        )}
        key={`word-${wordIndex}`}
        ref={ref}
      >
        {cleanedWord}
      </span>
    );
  }

  return (
    <span
      className={cn("effect-color -mx-3 cursor-pointer rounded-md px-3", {
        "bg-overlay-light": highlighted,
        "opacity-40": shouldFade,
      })}
      key={`word-${wordIndex}`}
      ref={ref}
    >
      {word}
    </span>
  );
};

WordRenderer.displayName = "WordRenderer";
